import { useCallback, useState, useEffect, useMemo } from "react";
import { Button, Flex, Separator, Spinner } from "@radix-ui/themes";
import { FieldGroup } from "@/v2/components/fields/field-group/FieldGroup";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { Pencil2Icon } from "@radix-ui/react-icons";
import { ConditionStep } from "./steps/ConditionStep";
import { ActionStep } from "./steps/ActionStep";
import { StrategyStep } from "./steps/StrategyStep";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import {
  EscalationCustomFieldFilters,
  EscalationRule,
} from "@/v2/entities/escalation-rule/EscalationRule.model";
import { useParams } from "react-router-dom";
export type EscalationRuleFormData = Omit<EscalationRule, "id"> & {
  customFieldFilters?: EscalationCustomFieldFilters;
};

const defaultValues: EscalationRuleFormData = {
  name: "New Custom Rule",
  topics: [],
  systemMessage:
    "I need to escalate this to a human agent. Please hold on while I connect you with someone who can help.",
  escalationStrategy: EscalationStrategy.NONE,
  escalationRuleOperations: [],
  subject: "",
};

interface EscalationRuleFormProps {
  initialValues?: EscalationRuleFormData;
  submitLabel?: string;
  loading?: boolean;
  allowLiveAgent?: boolean;
  onSubmit: (data: EscalationRuleFormData) => void | Promise<void>;
  onCancel?: () => void;
}

export function EscalationRuleForm({
  initialValues = defaultValues,
  submitLabel = "Create Escalation Rule",
  loading = false,
  allowLiveAgent = true,
  onSubmit,
  onCancel,
}: EscalationRuleFormProps) {
  const [formData, setFormData] =
    useState<EscalationRuleFormData>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { simulatorId } = useParams();
  // Update form data when initial values change (for edit mode)
  useEffect(() => {
    setFormData(initialValues);
  }, [initialValues]);

  const handleFormDataChange = useCallback(
    (updates: Partial<EscalationRuleFormData>) => {
      setFormData((prev) => {
        const newData = { ...prev, ...updates };

        if (updates.escalationStrategy === EscalationStrategy.LIVE_AGENT) {
          newData.systemMessage = "";
        }

        return newData;
      });
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    // Basic validation
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Rule name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare form data with proper systemMessage handling
    const submitData = { ...formData };

    // If escalation strategy is LIVE_AGENT, ensure systemMessage is empty string
    if (formData.escalationStrategy === EscalationStrategy.LIVE_AGENT) {
      submitData.systemMessage = "";
    }

    await onSubmit(submitData);
  }, [onSubmit, formData]);

  // Determine if the ActionStep should be visible
  const showActionStep = useMemo(() => {
    const isAutomatedMessage =
      formData.escalationStrategy === EscalationStrategy.NONE;

    const isCreateTicket =
      formData.escalationStrategy === EscalationStrategy.CREATE_TICKET &&
      formData.name !== "Default Rule";

    return isAutomatedMessage || isCreateTicket;
  }, [formData.escalationStrategy, formData.name]);

  return (
    <Flex direction={"column"}>
      <Button
        variant="solid"
        size="2"
        highContrast
        disabled={
          (loading ||
            (formData.topics.length === 0 &&
              !(
                formData.customFieldFilters &&
                Object.keys(formData.customFieldFilters).length > 0
              ))) &&
          formData.name !== "Default Rule"
        }
        onClick={handleSubmit}
        className="fixed top-[10px] right-[48px]"
      >
        <Spinner loading={loading} />
        {submitLabel}
      </Button>

      <Flex direction={"column"} gap={"8"}>
        <FieldGroup label="Rule Name" noSeparator error={errors.name}>
          <InlineTextField
            size={"5"}
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: value })}
            placeholder="Escalation Rule Name"
            icon={<Pencil2Icon width={24} height={24} className="ml-2" />}
            disabled={formData?.name === "Default Rule" || false}
          />
        </FieldGroup>

        <StrategyStep
          value={formData}
          simulatorId={simulatorId}
          onChange={handleFormDataChange}
          allowLiveAgent={allowLiveAgent}
        />

        <Separator orientation="horizontal" size="4" />

        <ConditionStep value={formData} onChange={handleFormDataChange} />

        {showActionStep && (
          <>
            <Separator orientation="horizontal" size="4" />
            <ActionStep
              value={formData}
              simulatorId={simulatorId}
              onChange={handleFormDataChange}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
}
